import { supabase, supabaseRpc } from '@/lib/supabase'

/**
 * Service for managing household data migrations and consistency checks
 */
export const householdMigrationService = {
    /**
     * Ensure all household owners are also members of their household
     * This fixes historical data where owners weren't added as members
     */
    async ensureOwnersAreMembers(): Promise<{ added: number; errors: string[] }> {
        const errors: string[] = []
        const added = 0

        try {
            // Get all households with explicit column selection to avoid ambiguity
            try {
                // First try using RPC to avoid ambiguity
                const { data: rpcHouseholds, error: rpcError } = await supabaseRpc.rpc(
                    'get_all_households_safe',
                    {}
                );

                if (!rpcError && rpcHouseholds) {
                    return await this.processHouseholdsForMembership(rpcHouseholds);
                }

                // If RPC fails, continue with direct query
                if (rpcError) {
                    console.error('RPC fetch failed, falling back to direct query:', rpcError);
                }
            } catch (rpcError) {
                console.error('Error trying RPC method for households:', rpcError);
                // Continue to direct query
            }

            // Try direct query with very explicit column selection
            const { data: households, error: householdsError } = await supabase
                .from('households')
                .select('id, owner_id, name, created_at')
                .order('created_at', { ascending: true })

            if (householdsError) {
                console.error('Error fetching households:', householdsError)
                console.error('SQL Error details:', {
                    code: householdsError.code,
                    message: householdsError.message,
                    details: householdsError.details,
                    hint: householdsError.hint
                })

                // If we still get an ambiguous column error, create a dummy result
                if (householdsError.code === '42702' && householdsError.message.includes('ambiguous')) {
                    // console.log('Ambiguous column error, skipping migration');
                    errors.push(`Failed to fetch households: ${householdsError.message}`);
                    return { added, errors };
                }

                errors.push(`Failed to fetch households: ${householdsError.message}`)
                return { added, errors }
            }

            if (!households || households.length === 0) {
                // console.log('No households found')
                return { added, errors }
            }

            return await this.processHouseholdsForMembership(households);
        } catch (error) {
            console.error('Fatal error during household owners migration:', error)
            errors.push(`Fatal error: ${error}`)
            return { added, errors }
        }
    },

    /**
     * Process households for membership checks and updates
     * Extracted to a separate method for reuse
     */
    async processHouseholdsForMembership(households: any[]): Promise<{ added: number; errors: string[] }> {
        const errors: string[] = []
        let added = 0

        // Check each household
        for (const household of households) {
            if (!household.owner_id) {
                console.warn(`Household ${household.id} has no owner_id, skipping`)
                errors.push(`Household "${household.name}" has no owner_id`)
                continue
            }

            try {
                // Check if owner is already a member using RPC to avoid ambiguity
                const { data: isMember, error: memberCheckError } = await supabaseRpc
                    .rpc('check_household_membership', {
                        p_household_id: household.id,
                        p_user_id: household.owner_id
                    })

                if (memberCheckError) {
                    console.error(`Error checking member for household ${household.id}:`, memberCheckError)
                    errors.push(`Failed to check member for household "${household.name}": ${memberCheckError.message}`)
                    continue
                }

                if (isMember) {
                    continue
                }

                // Owner is not a member, add them (use upsert to avoid duplicate key errors)
                const { error: insertError } = await supabase
                    .from('household_members')
                    .upsert({
                        household_id: household.id,
                        user_id: household.owner_id,
                        role: 'owner',
                        joined_at: new Date().toISOString(),
                        created_at: new Date().toISOString(),
                        created_by: household.owner_id
                    }, {
                        onConflict: 'household_id,user_id'
                    })

                if (insertError) {
                    // Ignorer les erreurs de duplicate key (code 23505) car elles signifient que le membre existe déjà
                    if (insertError.code !== '23505') {
                        console.error(`Error adding owner as member for household ${household.id}:`, insertError)
                        errors.push(`Failed to add owner as member for household "${household.name}": ${insertError.message}`)
                        continue
                    }
                    // Sinon, c'est juste un duplicate, on continue
                }

                // console.log(`Successfully added owner ${household.owner_id} as member of household ${household.id}`)
                added++

            } catch (error) {
                console.error(`Unexpected error processing household ${household.id}:`, error)
                errors.push(`Unexpected error processing household "${household.name}": ${error}`)
            }
        }

        return { added, errors }
    },

    /**
     * Check if a specific household owner is a member
     */
    async isOwnerMember(householdId: string): Promise<boolean> {
        try {
            // Get household owner with explicit column selection using RPC
            const { data: household, error: householdError } = await supabaseRpc
                .rpc('get_household_by_id_safe', {
                    p_household_id: householdId
                })

            if (householdError) {
                console.error('Error getting household owner:', householdError)
                return false
            }

            if (!household || household.length === 0 || !household[0].owner_id) {
                return false
            }

            const ownerId = household[0].owner_id

            // Check if owner is a member using RPC to avoid ambiguity
            const { data: isMember, error: memberError } = await supabaseRpc
                .rpc('check_household_membership', {
                    p_household_id: householdId,
                    p_user_id: ownerId
                })

            if (memberError) {
                console.error('Error checking if owner is member:', memberError)
                return false
            }

            return !!isMember
        } catch (error) {
            console.error('Error checking if owner is member:', error)
            return false
        }
    },

    /**
     * Ensure a specific household owner is a member
     * Returns true if owner was added, false if already a member or error
     */
    async ensureOwnerIsMember(householdId: string): Promise<boolean> {
        try {
            // Get household owner with explicit column selection using RPC
            const { data: household, error: householdError } = await supabaseRpc
                .rpc('get_household_by_id_safe', {
                    p_household_id: householdId
                })

            if (householdError) {
                console.error('Error getting household owner:', householdError)
                return false
            }

            if (!household || household.length === 0 || !household[0].owner_id) {
                // console.log(`No household found with ID ${householdId} or no owner_id`)
                return false
            }

            const ownerId = household[0].owner_id

            // Check if owner is already a member
            const isAlreadyMember = await this.isOwnerMember(householdId)
            if (isAlreadyMember) {
                return false // Already a member
            }

            // Add owner as member
            const { error: insertError } = await supabase
                .from('household_members')
                .insert({
                    household_id: householdId,
                    user_id: ownerId,
                    role: 'owner',
                    joined_at: new Date().toISOString(),
                    created_at: new Date().toISOString(),
                    created_by: ownerId
                })

            if (insertError) {
                console.error('Error adding owner as member:', insertError)
                return false
            }

            // console.log(`Successfully added owner ${ownerId} as member of household ${householdId}`)
            return true
        } catch (error) {
            console.error('Error ensuring owner is member:', error)
            return false
        }
    },

    /**
     * Get migration status for debugging
     */
    async getMigrationStatus(): Promise<{
        totalHouseholds: number
        householdsWithOwnerMembers: number
        householdsWithoutOwnerMembers: number
        householdsWithoutOwners: number
        errors?: string[]
    }> {
        const errors: string[] = [];

        try {
            // Try using RPC first to avoid ambiguity
            try {
                // console.log('Trying to fetch households for migration status using RPC...');
                const { data: rpcHouseholds, error: rpcError } = await supabaseRpc.rpc(
                    'get_all_households_safe',
                    {}
                );

                if (!rpcError && rpcHouseholds) {
                    // console.log(`Found ${rpcHouseholds.length} households using RPC for status check`);
                    return await this.processHouseholdsForStatus(rpcHouseholds);
                }

                if (rpcError) {
                    // console.log('RPC fetch failed for status check, falling back to direct query:', rpcError);
                }
            } catch (rpcError) {
                console.error('Error trying RPC method for status check:', rpcError);
                // Continue to direct query
            }

            // Get all households with explicit column selection
            const { data: households, error: householdsError } = await supabase
                .from('households')
                .select('id, owner_id')

            if (householdsError) {
                console.error('Error fetching households for status:', householdsError);

                // If we get an ambiguous column error, return a default status
                if (householdsError.code === '42702' && householdsError.message.includes('ambiguous')) {
                    // console.log('Ambiguous column error in getMigrationStatus, returning default status');
                    errors.push(`Failed to fetch households: ${householdsError.message}`);
                    return {
                        totalHouseholds: 0,
                        householdsWithOwnerMembers: 0,
                        householdsWithoutOwnerMembers: 0,
                        householdsWithoutOwners: 0,
                        errors
                    };
                }

                throw new Error(`Failed to fetch households: ${householdsError.message}`);
            }

            if (!households || households.length === 0) {
                return {
                    totalHouseholds: 0,
                    householdsWithOwnerMembers: 0,
                    householdsWithoutOwnerMembers: 0,
                    householdsWithoutOwners: 0
                };
            }

            return await this.processHouseholdsForStatus(households);
        } catch (error) {
            console.error('Error getting migration status:', error);
            return {
                totalHouseholds: 0,
                householdsWithOwnerMembers: 0,
                householdsWithoutOwnerMembers: 0,
                householdsWithoutOwners: 0,
                errors: [`Error getting migration status: ${error}`]
            };
        }
    },

    /**
     * Process households for status check
     * Extracted to a separate method for reuse
     */
    async processHouseholdsForStatus(households: any[]): Promise<{
        totalHouseholds: number
        householdsWithOwnerMembers: number
        householdsWithoutOwnerMembers: number
        householdsWithoutOwners: number
    }> {
        const totalHouseholds = households.length;
        let householdsWithOwnerMembers = 0;
        let householdsWithoutOwnerMembers = 0;
        let householdsWithoutOwners = 0;

        for (const household of households) {
            if (!household.owner_id) {
                householdsWithoutOwners++;
                continue;
            }

            const isOwnerMember = await this.isOwnerMember(household.id);
            if (isOwnerMember) {
                householdsWithOwnerMembers++;
            } else {
                householdsWithoutOwnerMembers++;
            }
        }

        return {
            totalHouseholds,
            householdsWithOwnerMembers,
            householdsWithoutOwnerMembers,
            householdsWithoutOwners
        };
    }
}